import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useClient } from '../../app/api';
import type { LiquidityDateRange } from '../../app/api/generated/client';
import {
    useLiquidityCalculatorCalculation,
    useLiquidityCashWithJbSettings,
    useLiquidityCashWithOtherBanksSettings,
    useLiquidityOneOffCashflowSettings,
    useLiquidityRecurringCashflowSettings,
    useUpdateLiquidityCalculatorCalculation,
} from '../api';
import { mutationKeys } from '../api/mutation-keys';
import { queryKeys } from '../api/query-keys';
import type { ILiquidityCalculationData } from '../interfaces';

/**
 * Single source of truth for the liquidity calculator view-model. All inputs are shared through the React Query cache,
 * so this hook is safe to call in any component under the calculator route — no props need to be drilled.
 */
export function useLiquidityCalculationData(): ILiquidityCalculationData {
    const { data: client } = useClient();
    const { data: calculation } = useLiquidityCalculatorCalculation();
    const { data: cashWithJbSettings } = useLiquidityCashWithJbSettings();
    const { data: cashWithOtherBanksSettings } = useLiquidityCashWithOtherBanksSettings();
    const { data: recurringCashflowSettings } = useLiquidityRecurringCashflowSettings();
    const { data: oneOffCashflowSettings } = useLiquidityOneOffCashflowSettings();
    const updateLiquidityCalculatorCalculation = useUpdateLiquidityCalculatorCalculation();

    // Reflect "recalculating" in every consumer (table, graph, …) regardless of which control triggered the change:
    // - the calculation mutation patches metrics on its own response (date-scope / cash-buffer),
    // - settings toggles patch their control optimistically, but the metrics only arrive once the calculation query
    //   refetches, so we also watch that in-flight refetch to keep the dim on until the new numbers land.
    const isMutatingCalculation = useIsMutating({ mutationKey: mutationKeys.updateLiquidityCalculatorCalculation() }) > 0;
    const isMutatingSettings = useIsMutating({ mutationKey: mutationKeys.updateLiquiditySettings() }) > 0;
    const isRefetchingCalculation = useIsFetching({ queryKey: queryKeys.liquidityCalculatorCalculation(client?.partnerUuid) }) > 0;
    const isUpdating = isMutatingCalculation || isMutatingSettings || isRefetchingCalculation;

    const updateScopeRange = useCallback(
        (scopeRange: LiquidityDateRange) => {
            const preferencesId = calculation?.preferences.id;
            if (preferencesId == null) return;

            updateLiquidityCalculatorCalculation.mutate({ id: preferencesId, scopeRange });
        },
        [calculation?.preferences.id, updateLiquidityCalculatorCalculation],
    );

    return useMemo<ILiquidityCalculationData>(() => {
        if (!calculation || !cashWithJbSettings || !cashWithOtherBanksSettings || !recurringCashflowSettings || !oneOffCashflowSettings)
            return { isLoading: true };

        const { metrics, preferences } = calculation;

        return {
            isLoading: false,
            calculatorPreferencesId: preferences.id,
            scopeRange: preferences.scopeRange,
            updateScopeRange,
            isUpdating,
            currency: client?.currency ?? 'CHF',
            cashWithJb: {
                amount: metrics.selectedCashJb,
                isEnabled: cashWithJbSettings.enabled ?? false,
            },
            cashWithOtherBanks: {
                amount: metrics.selectedCashOtherBanks,
                isEnabled: cashWithOtherBanksSettings.enabled ?? false,
            },
            totalSelectedCash: metrics.totalSelectedCash,
            totalSelectedCashPercentage: Math.round(metrics.totalSelectedCashPercentage ?? 0),
            totalCash: metrics.totalCash,
            totalCashPercentage: Math.round(metrics.totalCashPercentage ?? 0),
            liquidityNeed: {
                amount: metrics.liquidityNeed,
                isEnabled: (recurringCashflowSettings.enabled ?? false) || (oneOffCashflowSettings.enabled ?? false),
            },
            cashBuffer: metrics.cashBuffer,
            totalLiquidity: metrics.totalLiquidityNeed,
            totalLiquidityPercentage: Math.round(metrics.totalLiquidityNeedPercentage ?? 0),
            availableInvestmentAmount: metrics.availableInvestmentAmount,
            availableInvestmentPercentage: Math.round(metrics.availableInvestmentPercentage ?? 0),
            needCalculationSteps: metrics.needCalculationSteps,
        };
    }, [
        calculation,
        cashWithJbSettings,
        cashWithOtherBanksSettings,
        recurringCashflowSettings,
        oneOffCashflowSettings,
        client?.currency,
        updateScopeRange,
        isUpdating,
    ]);
}
